/** Which strike LADDER rung a trade bought: "ITM-3", "ATM", "OTM-1".
 *
 * The engines stamp it on the trade at entry (`strikeRung`) - they know it exactly when they pick
 * the contract, and nothing downstream can recompute it (POI anchors the strike on the candle's
 * level, not on the fill). Rows from before 2026-10-06 carry none; for those it is WORKED OUT from
 * the symbol's strike and the index level at entry, which is right except within a hair of a
 * rounding boundary, so the badge says so.
 */

import type { TradeLogEntry } from "@/types/tradeLog";

/** Strike step per index (contract_planner.spec_for). */
const STEP: Record<string, number> = { NIFTY: 50, SENSEX: 100 };

export interface Rung {
  label: string;
  /** True when worked out from the index level rather than recorded by the engine. */
  derived: boolean;
}

export function rungName(depth: number): string {
  if (depth === 0) return "ATM";
  return depth > 0 ? `ITM-${String(depth)}` : `OTM-${String(-depth)}`;
}

function derive(trade: TradeLogEntry): string | null {
  const symbol = trade.contractSymbol ?? "";
  const root = /^[A-Z]+/.exec(symbol)?.[0] ?? "";
  // A weekly symbol runs its expiry day straight into the strike (NIFTY26O06|22750PE), so
  // the strike is the last five digits - true of NIFTY and SENSEX at any level they trade.
  const tail = /(\d{5})(CE|PE)$/.exec(symbol);
  const [, strikeText = "", right = ""] = tail ?? [];
  const step = STEP[root];
  if (step === undefined || trade.signalEntry === null) return null;
  const atm = Math.round(trade.signalEntry / step) * step;
  const strike = Number(strikeText);
  const away = right === "CE" ? atm - strike : strike - atm;
  return rungName(Math.round(away / step));
}

/** The rung to show, or null when the trade is not a single option (a basket) or cannot say. */
export function rungOf(trade: TradeLogEntry): Rung | null {
  if (trade.strikeRung) return { label: trade.strikeRung, derived: false };
  if (trade.horizon !== "INTRADAY") return null;
  const label = derive(trade);
  return label === null ? null : { label, derived: true };
}
